/**
 * Serverzustand des Ticketmoduls.
 *
 * Der Schluessel enthaelt die Filter. Das ist mehr als Buchfuehrung: Zwei
 * Listen mit verschiedenen Filtern sind zwei verschiedene Datenbestaende, und
 * ein gemeinsamer Schluessel liesse sie einander ueberschreiben.
 */

import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect } from 'react';

import { ApiError } from '../../lib/apiError';
import {
  acquireTicketLock,
  bulkUpdateTickets,
  createTicket,
  downloadTicketPdf,
  findDuplicates,
  getBoard,
  getTicketFilterCounts,
  getTicketTabCounts,
  getTicket,
  getTicketHistory,
  listTickets,
  lookupTickets,
  mergeTicket,
  performTicketAction,
  releaseTicketLock,
  setTicketParent,
  setTicketTags,
  setTicketWatching,
  splitTicket,
  updateTicket,
  type BulkUpdateRequest,
  type TicketAction,
  type TicketCreate,
  type TicketFilterCountQuery,
  type TicketListQuery,
  type TicketUpdate,
} from './api';
import { saveDownload } from './articles/attachmentFile';

export const ticketKeys = {
  all: ['tickets'] as const,
  list: (query: TicketListQuery) => ['tickets', 'list', query] as const,
  detail: (ticketId: string) => ['tickets', 'detail', ticketId] as const,
  duplicates: (title: string) => ['tickets', 'duplicates', title] as const,
  history: (ticketId: string) => ['tickets', 'history', ticketId] as const,
  counts: (ticketId: string) => ['tickets', 'counts', ticketId] as const,
  filterCounts: (query: TicketFilterCountQuery) => ['tickets', 'filter-counts', query] as const,
  board: (query: BoardQuery) => ['tickets', 'board', query] as const,
};

export interface BoardQuery {
  queue_id?: string;
  owner_id?: string;
  customer_id?: string;
}

const retryUnlessPermanent = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && error.isPermanent) && failureCount < 2;

/**
 * Die Ticketliste, seitenweise nachladbar.
 *
 * ``enabled`` haelt die Abfrage zurueck, solange eine Angabe der Einschraenkung
 * noch fehlt. Das ist kein Feinschliff: Die Arbeitsliste der Startseite
 * schraenkt auf die eigene Kennung ein, und die steht erst fest, wenn die
 * Sitzung geladen ist. Ohne die Bremse liefe **eine erste Abfrage ohne diese
 * Einschraenkung** -- und "Meine Arbeit" zeigte fuer einen Moment die
 * Vorgaenge aller Kollegen.
 */
export function useTicketList(query: TicketListQuery, options: { enabled?: boolean } = {}) {
  return useInfiniteQuery({
    queryKey: ticketKeys.list(query),
    queryFn: ({ pageParam }) => listTickets(pageParam ? { ...query, cursor: pageParam } : query),
    initialPageParam: undefined as string | undefined,
    // Der Cursor ist fuer uns undurchsichtig -- er wird weitergereicht, nicht
    // ausgewertet (docs/05, Abschnitt 3).
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled: options.enabled ?? true,
    retry: retryUnlessPermanent,
  });
}

/**
 * Die Zahlen ueber den Reitern der Ticketliste.
 *
 * **Kein Abruf im Hintergrund.** Geholt wird beim Laden und nach jeder
 * eigenen Aenderung: Der Schluessel liegt unter ``ticketKeys.all``, und jede
 * Aenderung an einem Ticket erklaert diesen Zweig fuer ungueltig. Was ein
 * Kollege aendert, erscheint mit dem naechsten Aufruf der Liste -- so wie die
 * Zeilen darunter auch.
 */
export function useTicketFilterCounts(
  query: TicketFilterCountQuery,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ticketKeys.filterCounts(query),
    queryFn: () => getTicketFilterCounts(query),
    enabled: options.enabled ?? true,
    retry: retryUnlessPermanent,
  });
}

export function useTicket(ticketId: string) {
  return useQuery({
    queryKey: ticketKeys.detail(ticketId),
    queryFn: () => getTicket(ticketId),
    retry: retryUnlessPermanent,
  });
}

/**
 * Die Zahlen an den Reitern eines Tickets -- F1-55.
 *
 * **Neu gezaehlt wird nach jeder erfolgreichen Aenderung.** Termine, Zeiten,
 * Anhaenge, Teilaufgaben und Genehmigungen haengen an eigenen
 * Abfrageschluesseln in eigenen Modulen; jede ihrer Aenderungen einzeln
 * hierher zu melden hiesse, an zwanzig Stellen daran zu denken -- und an der
 * einundzwanzigsten stuende die Blase falsch. Stattdessen hoert die Seite auf
 * den Zwischenspeicher der Aenderungen: Meldet einer Erfolg, holt sie die
 * Zahlen neu -- eine Anfrage je abgeschlossener Handlung, nur solange das
 * Ticket offen ist.
 *
 * Auf das Ereignis und nicht auf ``useIsMutating``: Eine schnelle Aenderung
 * laeuft zwischen zwei Darstellungen an und wieder aus, die Zahl der
 * laufenden Aenderungen steht vorher und nachher auf null -- und der
 * Wechsel, auf den man warten wollte, wird nie sichtbar.
 */
export function useTicketTabCounts(ticketId: string) {
  const queryClient = useQueryClient();

  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (event.type === 'updated' && event.action.type === 'success') {
          void queryClient.invalidateQueries({ queryKey: ticketKeys.counts(ticketId) });
        }
      }),
    [queryClient, ticketId],
  );

  return useQuery({
    queryKey: ticketKeys.counts(ticketId),
    queryFn: () => getTicketTabCounts(ticketId),
    retry: retryUnlessPermanent,
  });
}

export function useCreateTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: TicketCreate) => createTicket(payload),
    onSuccess: async (ticket) => {
      queryClient.setQueryData(ticketKeys.detail(ticket.id), ticket);
      await queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

export function useUpdateTicket(ticketId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payload, version }: { payload: TicketUpdate; version: number }) =>
      updateTicket(ticketId, payload, version),
    onSuccess: async (ticket) => {
      queryClient.setQueryData(ticketKeys.detail(ticketId), ticket);
      await queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

export function useTicketAction(ticketId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (action: TicketAction) => performTicketAction(ticketId, action),
    onSuccess: async (ticket) => {
      queryClient.setQueryData(ticketKeys.detail(ticketId), ticket);
      await queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

/**
 * Holt den Ausdruck und uebergibt ihn dem Browser -- F1-35, K-39.
 *
 * Als Mutation und nicht als blosser Aufruf: Scheitert der Abruf, muss das
 * zu sehen sein. Ein verschluckter Fehler sieht fuer den Anwender aus wie ein
 * Knopf, der nichts tut -- so blieb ein fehlendes Pango auf dem Server
 * unbemerkt.
 */
export function useTicketPdf(ticketId: string) {
  return useMutation({
    mutationFn: ({
      includeInternal,
      fallbackName,
    }: {
      includeInternal: boolean;
      fallbackName: string;
    }) => saveDownload(() => downloadTicketPdf(ticketId, includeInternal), fallbackName),
  });
}

/**
 * Die Historie eines Tickets -- F1-33.
 *
 * Sie kommt aus `audit_log` und nicht aus einer zweiten Quelle. Deshalb wird
 * sie nach jeder Aenderung am Ticket ungueltig: Ein Verlauf, der den letzten
 * Schritt nicht zeigt, ist schlimmer als keiner -- er behauptet
 * Vollstaendigkeit.
 */
export function useTicketHistory(ticketId: string, enabled = true) {
  return useQuery({
    queryKey: ticketKeys.history(ticketId),
    queryFn: () => getTicketHistory(ticketId),
    retry: retryUnlessPermanent,
    enabled,
  });
}

export function useBoard(query: BoardQuery) {
  return useQuery({
    queryKey: ticketKeys.board(query),
    queryFn: () => getBoard(query),
    retry: retryUnlessPermanent,
  });
}

export function useSetTicketTags(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tagIds: string[]) => setTicketTags(ticketId, tagIds),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
    },
  });
}

export function useSetTicketWatching(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (watching: boolean) => setTicketWatching(ticketId, watching),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
    },
  });
}

/**
 * Haelt die weiche Bearbeitungssperre, solange die Seite offen ist.
 *
 * Sie wird beim Betreten geholt und beim Verlassen zurueckgegeben. Erneuert
 * wird sie nicht: Fuenf Minuten sind bewusst kurz gewaehlt, und eine Sperre,
 * die sich selbst verlaengert, solange ein Reiter im Hintergrund offen steht,
 * meldet Arbeit, die niemand tut.
 */
export function useTicketLock(ticketId: string, active: boolean) {
  const query = useQuery({
    queryKey: ['tickets', 'lock', ticketId],
    queryFn: () => acquireTicketLock(ticketId),
    enabled: active,
    // Fehlschlaege nicht wiederholen: Ein 409 heisst, jemand anders hat sie.
    retry: false,
    refetchOnWindowFocus: false,
    gcTime: 0,
  });

  useEffect(() => {
    if (!active) return undefined;
    return () => {
      // Beim Verlassen freigeben. Scheitert das -- etwa weil die Sitzung
      // gerade abgelaufen ist --, laeuft die Sperre von selbst ab.
      void releaseTicketLock(ticketId).catch(() => undefined);
    };
  }, [ticketId, active]);

  return query;
}

/**
 * Tickets fuer ein Auswahlfeld -- "57" findet "T-2026-000057".
 *
 * Ohne Eingabe die zuletzt geaenderten: Ein Zielticket, an dem gerade
 * gearbeitet wird, steht dann ohne Tippen oben.
 */
export function useTicketLookup(term: string, enabled = true) {
  return useQuery({
    queryKey: ['tickets', 'lookup', term],
    queryFn: () => lookupTickets(term),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
    retry: retryUnlessPermanent,
  });
}

export function useMergeTicket(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // Die Kennung des im Auswahlfeld gewaehlten Tickets -- siehe ``TicketCombobox``.
    mutationFn: (targetTicketId: string) => mergeTicket(ticketId, targetTicketId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

export function useSplitTicket(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { title: string; article_ids: string[] }) =>
      splitTicket(ticketId, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

/**
 * Moegliche Duplikate zu einem Betreff -- F1-28.
 *
 * Erst ab drei Zeichen: Der Server verlangt sie, und eine Abfrage je
 * getipptem Buchstaben waere ohnehin eine Abfrage zu viel.
 */
export function useDuplicateCandidates(title: string) {
  return useQuery({
    queryKey: ticketKeys.duplicates(title),
    queryFn: () => findDuplicates(title),
    enabled: title.trim().length >= 3,
    // Eine halbe Minute: Waehrend die Maske ausgefuellt wird, aendert sich
    // der Bestand kaum -- und der Betreff aendert sich bei jedem Zeichen.
    staleTime: 30_000,
    retry: retryUnlessPermanent,
  });
}

export function useSetTicketParent(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (parentTicketId: string | null) => setTicketParent(ticketId, parentTicketId),
    onSuccess: async () => {
      // Beide Seiten aendern sich: Das Kind bekommt einen Elternvorgang, der
      // Elternvorgang ein Kind -- und dessen Ansicht steht womoeglich offen.
      await queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

export function useBulkUpdate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: BulkUpdateRequest) => bulkUpdateTickets(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}
